import React, { useState, useEffect, useCallback } from 'react';
import { locationsAPI, reportsAPI } from '../../services/garageApi';
import BackButton from '../common/BackButton';

const InventoryReport = () => {
    const [summary, setSummary] = useState([]);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [locationFilter, setLocationFilter] = useState('');
    const [locations, setLocations] = useState([]);

    const fetchLocations = async () => {
        try {
            const response = await locationsAPI.getAll();
            setLocations(response.data || []);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const fetchInventorySummary = useCallback(async () => {
        try {
            const params = {};
            if (locationFilter) params.location_id = locationFilter;

            const response = await reportsAPI.inventorySummary(params);
            setSummary(response.data.summary || []);
            setLowStockItems(response.data.low_stock_items || []);
        } catch (error) {
            console.error('Error fetching inventory summary:', error);
        } finally {
            setLoading(false);
        }
    }, [locationFilter]);

    useEffect(() => {
        fetchLocations();
        fetchInventorySummary();
    }, [fetchInventorySummary]);

    const getProductName = (item) => {
        if (!item || !item.inventoryable) return '-';
        const product = item.inventoryable;
        const sizeType = [product.size ? `${product.size}"` : '', product.type].filter(Boolean).join(' ');
        return product.name || product.battery_name || product.service_name || sizeType || '-';
    };

    const getTotalByType = (type) => {
        return summary
            .filter(s => s.product_type === type)
            .reduce((sum, item) => sum + parseInt(item.total_quantity), 0);
    };

    const getUniqueProductsByType = (type) => {
        return summary
            .filter(s => s.product_type === type)
            .reduce((sum, item) => sum + parseInt(item.unique_products), 0);
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading report...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Report Header */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        <BackButton />
                        <h2 className="text-2xl font-bold text-gray-900">Inventory Summary Report</h2>
                    </div>
                    <div className="text-sm text-gray-500">
                        Generated: {new Date().toLocaleDateString()}
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Location</label>
                    <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="">All Locations</option>
                        {locations.map(location => (
                            <option key={location.id} value={location.id}>{location.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Tires</p>
                            <p className="text-3xl font-bold text-gray-900">{getTotalByType('Tires')}</p>
                            <p className="text-sm text-gray-500 mt-1">{getUniqueProductsByType('Tires')} unique products</p>
                        </div>
                        <div className="text-4xl">🛞</div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Batteries</p>
                            <p className="text-3xl font-bold text-gray-900">{getTotalByType('Batteries')}</p>
                            <p className="text-sm text-gray-500 mt-1">{getUniqueProductsByType('Batteries')} unique products</p>
                        </div>
                        <div className="text-4xl">🔋</div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Wipers</p>
                            <p className="text-3xl font-bold text-gray-900">{getTotalByType('Wipers')}</p>
                            <p className="text-sm text-gray-500 mt-1">{getUniqueProductsByType('Wipers')} unique products</p>
                        </div>
                        <div className="text-4xl">🌧️</div>
                    </div>
                </div>
            </div>

            {/* Location Breakdown */}
            {summary.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Inventory by Location</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Quantity</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Products</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {summary.map((item, index) => (
                                <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div>{item.location?.name || 'All Locations'}</div>
                                        {item.zone && !item.zone.is_system && (
                                            <div className="text-xs text-gray-400">{item.zone.name}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{item.product_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.total_quantity}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{item.unique_products}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Low Stock Alert */}
            {lowStockItems.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 text-red-700">Low Stock Items</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-red-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {lowStockItems.map(item => (
                                <tr key={item.id}>
                                    <td className="px-6 py-4 text-sm">{getProductName(item)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div>{item.location?.name || '-'}</div>
                                        {item.zone && !item.zone.is_system && (
                                            <div className="text-xs text-gray-400">{item.zone.name}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                                        {item.quantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        Critical
                      </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Print Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => window.print()}
                    className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                >
                    Print Report
                </button>
            </div>
        </div>
    );
};

export default InventoryReport;